import React, { useCallback, useEffect, useRef, useState } from "react";
import { WarningCircle, ArrowClockwise, Television, ArrowsOut, ArrowsIn } from "@phosphor-icons/react";
import { adminApi } from "@/lib/api";
import { useCommandFeed } from "@/hooks/useCommandFeed";
import { CommandCentre } from "@/components/admin/command/CommandCentre";
import { ScaleCanvas } from "@/components/admin/command/ScaleCanvas";
import { DisplayLinkDialog } from "@/components/admin/command/DisplayLinkDialog";

const btn = "inline-flex items-center gap-1.5 border border-neutral-700 text-neutral-200 rounded-full px-3.5 py-1.5 text-xs hover:border-[#73bf44] hover:text-white transition-colors";

function useFullscreen(ref) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === ref.current);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, [ref]);
  const toggle = () => (full ? document.exitFullscreen() : ref.current?.requestFullscreen?.())?.catch?.(() => {});
  return [full, toggle];
}

function Status({ error, onRetry }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-4 text-center p-8" data-testid={error ? "dashboard-error" : "dashboard-loading"}>
      {error ? <WarningCircle size={36} className="text-amber-400" weight="duotone" /> : <span className="w-8 h-8 rounded-full border-2 border-neutral-800 border-t-[#73bf44] animate-spin" />}
      <div className="text-neutral-300">{error ? "Couldn't load the Command Centre. Please check your connection and try again." : "Loading the Command Centre…"}</div>
      {error && <button onClick={onRetry} data-testid="dashboard-retry" className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-5 py-2.5 text-sm hover:bg-[#5fa338]"><ArrowClockwise size={15} /> Retry</button>}
    </div>
  );
}

export default function Dashboard() {
  const fetcher = useCallback(() => adminApi.get("/admin/command-centre"), []);
  const { data, error, reload } = useCommandFeed(fetcher);
  const [linkOpen, setLinkOpen] = useState(false);
  const ref = useRef(null);
  const [full, toggleFull] = useFullscreen(ref);

  if (!data) return <Status error={error} onRetry={reload} />;

  const actions = (
    <div className="flex items-center gap-2 pl-1">
      {!full && <button className={btn} onClick={() => setLinkOpen(true)} data-testid="cc-tv-link-btn"><Television size={14} /> TV link</button>}
      <button className={btn} onClick={toggleFull} data-testid="cc-fullscreen-btn">{full ? <ArrowsIn size={14} /> : <ArrowsOut size={14} />} {full ? "Exit" : "Full screen"}</button>
    </div>
  );

  return (
    <div ref={ref} className="h-full bg-[#050505]" data-testid="dashboard-page">
      <ScaleCanvas>
        <CommandCentre data={data} stale={!!error} mode="admin" actions={actions} />
      </ScaleCanvas>
      <DisplayLinkDialog open={linkOpen} onOpenChange={setLinkOpen} onSaved={reload} />
    </div>
  );
}
